import React, { useEffect, useMemo, useState } from 'react';
import {
  getSnackBar,
  useNotification,
  getButtonSpinner,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import {
  LabelValue,
  getCurrentWeek,
  formatDateString,
  SsaStudentProfileLoader,
} from '../../ssaCommonUtils';
import {
  ProfileCard,
  GridRightAction,
  LiveInfoWrapper,
  DataGridWrapper,
  InComplianceGrid,
} from './SsaAttendanceTabStyled';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { intialPageSize } from 'constants';
import {
  getStudentDetails,
  getStudentCourseData,
} from 'layouts/students/service/studentsApi';
import MDTypography from 'components/MDTypography';
import { EmptyState } from './SsaAtendanceTabUtils';
import { useSelector, useDispatch } from 'react-redux';
import CustomizedSnackbars from 'components/snackbar/Snackbar';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import PreviousWeekNoteHistory from './PreviousWeekNoteHistory';
import {
  getAttendanceWeeksOptions,
  postStudentAttendanceNote,
} from 'layouts/applications/attendance/services/attendanceApis';
import StudentTrackersAccordion from './StudentTrackersAccordion';
import CannedResponsesAccordion from './CannedResponsesAccordion';
import { attendanceCourseColumns } from './attendanceCourseColumns';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import AssetNotes from 'layouts/applications/attendance/AttendanceNotes';
import { FormInputSwitch } from 'components/Common/Forms/FormInputSwitch';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import {
  getCourseChecks,
  postCourseChecks,
  deleteCourseCheck,
} from 'layouts/studentSuccessAdvocate/FilterPresets/services/courseChecksApi';
import { attendanceStatusOptions } from 'layouts/applications/attendance/common/constants';
import { getStudentTrackerColumns } from 'layouts/studentTracking/services/studentTrackerApi';
import { getCannedResponses } from 'layouts/studentSuccessAdvocate/cannedResponses/services/cannedResponsesApi';
import { setSelectedSsaStudentDetails } from 'layouts/studentSuccessAdvocate/ssa/store/ssaStudentDetailSlice/ssaStudentDetailSlice';
import { setSidebarSelectedStudent } from 'layouts/studentSuccessAdvocate/ssa/store/ssaSidenavBarStudentDetails/ssaSidenavBarStudentDetailsSlice';

const SsaAttendanceTab = ({ onError, onErrorMessage }) => {
  const selectedId = useSelector(
    (state) => state?.ssaStudentDetail?.selectedSsaStudent || '',
  );
  const selectedSsaStudentDetails = useSelector(
    (state) => state?.ssaStudentDetail?.selectedSsaStudentDetails || {},
  );
  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {},
  );
  const sidebarStudents = useSelector(
    (state) => state?.ssaSidenavBarStudentDetails?.sideBarSelectedStudent || [],
  );
  const selectedTrackers = useSelector(
    (state) => state?.ssaStudentTrackers?.selectedStudentTrackers || [],
  );

  const { rawProgressData } = selectedSsaStudentDetails || {};
  const { dataFrom: CachedFromDate = '' } = rawProgressData || {};

  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const [notesData, setNotesData] = useState(null);
  const [weekOptions, setWeekOptions] = useState([]);
  const [liveCourseData, setLiveCourseData] = useState({});
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [trackerStudentData, setTrackerStudentData] = useState([]);
  const [studentProfileDetail, setStudentProfileDetail] = useState({});
  const [updateButtonLoading, setUpdateButtonLoading] = useState(false);
  const [attendanceTabLoading, setAttendanceTabLoading] = useState(false);
  const [cannedResponsesListData, setCannedResponsesListData] = useState([]);
  const [courseListData, setCourseListData] = useState({
    rows: [],
    pageSize: intialPageSize,
    isLoading: false,
  });
  const [showLiveColumns, setShowLiveColumns] = useState(false);

  useEffect(() => {
    if (selectedId) {
      fetchStudentAttendance();
      fetchCannedResponsesListData();
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedTrackers?.length && selectedId) {
      fetchTrackerStudentData();
    }
  }, [selectedTrackers, selectedId]);

  const fetchTrackerStudentData = async () => {
    try {
      const responses = await Promise.all(
        selectedTrackers?.map((tracker) =>
          getStudentTrackerColumns(tracker?.trackerId, selectedId),
        ),
      );

      const formatted = responses?.map((res, index) => {
        const tracker = selectedTrackers[index];

        return {
          name: tracker?.name,
          data: res?.data || {},
          columns: tracker?.columns,
          version: tracker?.version,
          trackerId: tracker?.trackerId,
          statusOptions: tracker?.statusOptions,
        };
      });

      setTrackerStudentData(formatted);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchStudentAttendance = async () => {
    try {
      setAttendanceTabLoading(true);
      const getStudentProfileDetails = await getStudentDetails(selectedId);
      const item = getStudentProfileDetails?.item;

      setStudentProfileDetail(item);
      dispatch(setSelectedSsaStudentDetails(item));

      // Attendance notes (existing saved weeks)
      const attendanceNotes = item?.attendanceData?.notes || [];
      const weeks = attendanceNotes.map((note) => ({
        id: note?.week,
        value: note?.week,
        label: note?.week,
      }));

      const allWeeksResponse = await getAttendanceWeeksOptions(
        ssaStudentDetail?.semester,
      );

      const allWeeks = allWeeksResponse.map((w) => ({
        id: w?.descr,
        value: w?.descr,
        label: w?.descr,
      }));
      setWeekOptions(allWeeks);

      // ✅ Always select latest week (index 0)
      const currentWeek = getCurrentWeek(allWeeksResponse);

      if (currentWeek) {
        setValue('week', currentWeek?.descr);
      } else if (allWeeksResponse?.length > 0) {
        setValue('week', allWeeksResponse[allWeeksResponse.length - 1].descr);
      }

      const rows =
        item?.attendanceData?.courseInfo?.map((course, index) => ({
          id: index,
          courseName: course?.courseName || '',
          actualGrade: course?.actualGrade,
          lastOnlineActivity: course?.lastOnlineActivity
            ? format(new Date(course?.lastOnlineActivity), 'MM-dd-yyyy hh:mm a')
            : '',
          numSchoolDaysSinceOnline: course?.numSchoolDaysSinceOnline,
        })) || [];

      setCourseListData((prev) => ({
        ...prev,
        rows,
      }));

      // if (weeks?.length > 0) setValue('week', weeks[0]?.id);

      setAttendanceTabLoading(false);
      onError(false);
      onErrorMessage('');
    } catch (error) {
      const msg =
        error?.response?.data?.message || 'Failed to load attendance.';
      onError(true);
      onErrorMessage(msg); // <-- SEND MESSAGE TO PARENT
      setAttendanceTabLoading(false);
      handleErrorResponse(msg);
    }
  };

  const defaultValue = {
    week: '',
    note: '',
    label: '',
    inCompliance: false,
    fetchLiveData: false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { handleSubmit, control, formState, setValue, watch, reset } =
    useFunction;
  const { errors } = formState;

  const watchedWeek = watch('week');
  const watchedFetchLiveData = watch('fetchLiveData');

  useEffect(() => {
    if (selectedId) {
      reset(defaultValue);
      setNotesData(null);
      setLiveCourseData({});
      setShowLiveColumns(false);
    }
  }, [selectedId, reset]);

  useEffect(() => {
    if (
      selectedId &&
      watchedWeek &&
      ssaStudentDetail?.semester &&
      courseListData?.rows?.length > 0
    ) {
      fetchStudentCourseChecks();
    }
  }, [
    selectedId,
    watchedWeek,
    courseListData?.rows, // ✅ RUN AGAIN when rows are available
    ssaStudentDetail?.semester,
  ]);

  useEffect(() => {
    if (watchedFetchLiveData && selectedId) {
      getStudentLiveCourseData();
    } else {
      setShowLiveColumns(false);
    }
  }, [watchedFetchLiveData, selectedId]);

  const fetchCannedResponsesListData = async () => {
    try {
      const data = await getCannedResponses();
      setCannedResponsesListData(data);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchStudentCourseChecks = async () => {
    setCourseListData((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await getCourseChecks(
        selectedId,
        watchedWeek,
        ssaStudentDetail?.semester,
      );

      const checkedCourseNames = Object.keys(result?.checkedCourses || {});

      // Match names and get row ids from DataGrid rows
      const matchedIds = courseListData.rows
        .filter((row) =>
          checkedCourseNames.some((name) => row.courseName === name),
        )
        .map((row) => row.id);

      setSelectedCourseIds(matchedIds);
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  // Update form when week is selected
  useEffect(() => {
    if (watchedWeek && studentProfileDetail?.attendanceData?.notes) {
      const weekData = studentProfileDetail?.attendanceData?.notes.find(
        (note) => note?.week === watchedWeek,
      );

      if (weekData) {
        setValue('note', weekData?.note);
        setValue('label', weekData?.label);
        setValue('inCompliance', weekData?.inCompliance);
        setNotesData(weekData);
      }
    }
  }, [watchedWeek, studentProfileDetail, setValue]);

  const handleUpdateWeekNote = async (data) => {
    const snackBar = getSnackBar('Successfully attendance note is saved');
    const { note, week, label, inCompliance } = data || {};
    const payload = {
      note: note ?? '',
      week: week ?? '',
      label: label ?? '',
      inCompliance: inCompliance ?? false,
      semester: ssaStudentDetail?.semester,
    };
    try {
      setUpdateButtonLoading(true);
      await postStudentAttendanceNote(payload, selectedId);
      dispatch(displaySnackbar(snackBar));
      await fetchStudentAttendance();
      setValue('fetchLiveData', false);

      const updatedSidebarStudents = sidebarStudents?.map((student) =>
        student?.id === selectedId
          ? { ...student, inCompliance: inCompliance }
          : student,
      );
      dispatch(setSidebarSelectedStudent(updatedSidebarStudents));

      setUpdateButtonLoading(false);
    } catch (error) {
      setUpdateButtonLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const pinnedResponses = useMemo(
    () => cannedResponsesListData?.filter((item) => item?.pinned),
    [cannedResponsesListData],
  );

  const handleToggleSelect = async (id, checked) => {
    const selectedRow = courseListData?.rows?.find((row) => row?.id === id);
    if (!selectedRow) return;

    const payload = {
      courseName: selectedRow?.courseName,
      week: watchedWeek,
      semester: ssaStudentDetail?.semester,
    };

    try {
      if (checked) {
        setCourseListData((prev) => ({ ...prev, isLoading: true }));
        await postCourseChecks(selectedId, payload);

        setSelectedCourseIds((prev) => [...prev, id]);
        setCourseListData((prev) => ({ ...prev, isLoading: false }));
      } else {
        setCourseListData((prev) => ({ ...prev, isLoading: true }));
        await deleteCourseCheck(
          selectedId,
          selectedRow.courseName,
          watchedWeek,
          ssaStudentDetail?.semester,
        );

        setSelectedCourseIds((prev) => prev.filter((x) => x !== id));
        setCourseListData((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const getStudentLiveCourseData = async () => {
    try {
      setCourseListData((prev) => ({ ...prev, isLoading: true }));
      setLiveCourseData({});
      const response = await getStudentCourseData(selectedId);
      setLiveCourseData(response);

      const liveMap = {};
      response?.courseData?.forEach((c) => {
        liveMap[c.courseName] = c;
      });

      setCourseListData((prev) => ({
        ...prev,
        rows: prev?.rows?.map((row) => {
          const live = liveMap[row?.courseName];

          return {
            ...row,
            // ---- LIVE FIELDS ----
            liveDays: live?.numSchoolDaysSinceOnlineCalculated ?? 0,
            liveGrade: live?.actualGrade ?? '',
            liveLastActivity: live?.lastOnlineActivity
              ? format(new Date(live?.lastOnlineActivity), 'MM-dd-yyyy hh:mm a')
              : '',
          };
        }),
        isLoading: false,
      }));
      setShowLiveColumns(true);
    } catch (error) {
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const pageSizeChange = pageSizeChangeHandler(setCourseListData);

  // Prevent premature render before student data is ready
  if (!selectedId || !selectedSsaStudentDetails?.studentId) {
    return <SsaStudentProfileLoader />;
  }

  if (attendanceTabLoading) {
    return <SsaStudentProfileLoader />;
  }

  const providersList = Array.isArray(liveCourseData.providersQueried)
    ? liveCourseData.providersQueried.join(', ')
    : '-';

  const liveFetchedDate = formatDateString(liveCourseData?.fetchedAt);
  const isCachedFromDate = formatDateString(CachedFromDate);

  return (
    <>
      <CustomizedSnackbars />
      <Grid container spacing={2}>
        {/* Attendance Data Card */}
        <Grid item xs={12} md={4}>
          <ProfileCard>
            <MDTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ mb: 0 }}
            >
              Sem: {ssaStudentDetail?.semester} | Week: {watchedWeek}
            </MDTypography>

            <Grid container spacing={0} mt={2}>
              {/* <Grid item xs={12}>
            <FormInputDropdown
              name="week"
              disabled={true}
              control={control}
              label="Select Week"
              options={weekOptions}
              errors={!!errors.week}
              helperText={errors?.week?.message}
            />
          </Grid> */}

              <Grid
                item
                xs={12}
                sx={{ '& .MuiFormControl-root': { mb: 0, mt: -1 } }}
              >
                <FormInputDropdown
                  name="label"
                  control={control}
                  errors={!!errors.label}
                  label="Attendance Status"
                  options={attendanceStatusOptions}
                  helperText={errors?.label?.message}
                />
              </Grid>
              <InComplianceGrid item xs={12}>
                <FormInputSwitch
                  control={control}
                  name="inCompliance"
                  label="In Compliance"
                />
              </InComplianceGrid>
              <Grid item xs={12}>
                <FormInputText
                  multiline
                  name="note"
                  minRows={10}
                  maxRows={10}
                  control={control}
                  errors={!!errors?.note}
                  label="Attendance Note"
                  helperText={errors?.note?.message}
                />
              </Grid>

              <GridRightAction item xs={12} mt={-2.5} mb={-2}>
                {getButtonSpinner(
                  updateButtonLoading,
                  handleSubmit(handleUpdateWeekNote),
                  'Update Attendance',
                )}
              </GridRightAction>
            </Grid>
          </ProfileCard>
        </Grid>

        {/* Live Data Card */}
        <Grid item xs={12} md={8}>
          <ProfileCard>
            <Grid item xs={12} md={3} mb={-8} mt={-2}>
              <FormInputSwitch
                control={control}
                name="fetchLiveData"
                label="Fetch Live Data"
              />
            </Grid>
            <LiveInfoWrapper>
              <LabelValue label="Cached from" value={isCachedFromDate} />

              {showLiveColumns && (
                <>
                  &nbsp;|&nbsp;
                  <LabelValue label="Live Fetched" value={liveFetchedDate} />
                  &nbsp;|&nbsp;
                  <LabelValue label="Providers" value={providersList} />
                </>
              )}
            </LiveInfoWrapper>

            <DataGridWrapper>
              <DeltaDataGrid
                listData={courseListData}
                columns={attendanceCourseColumns(
                  selectedCourseIds,
                  handleToggleSelect,
                  showLiveColumns,
                )}
                setListData={pageSizeChange}
                defaultSortField="courseName"
                visibleRowCount={4}
              />
            </DataGridWrapper>
          </ProfileCard>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Pinned Responses Card */}
            {pinnedResponses?.length > 0 && (
              <Grid item xs={12}>
                <ProfileCard>
                  <CannedResponsesAccordion
                    cannedResopnse={cannedResponsesListData}
                  />
                </ProfileCard>
              </Grid>
            )}

            {/* Notes Card */}
            <Grid item xs={12}>
              <ProfileCard>
                {notesData ? (
                  <AssetNotes notesData={notesData} />
                ) : (
                  <EmptyState message="ℹ️ No existing note for this week" />
                )}
              </ProfileCard>
            </Grid>
          </Grid>
        </Grid>

        {/* Previous Week Note History */}
        <Grid item xs={12}>
          <ProfileCard>
            <PreviousWeekNoteHistory
              heading="Previous Week Note History"
              notesData={studentProfileDetail?.attendanceData?.notes}
            />
          </ProfileCard>
        </Grid>

        {/* Student Trackers */}
        {trackerStudentData?.length > 0 && (
          <Grid item xs={12}>
            <ProfileCard>
              <StudentTrackersAccordion
                trackers={trackerStudentData}
                fetchTrackerStudentData={fetchTrackerStudentData}
              />
            </ProfileCard>
          </Grid>
        )}
      </Grid>
      <NotificationPopup />
    </>
  );
};

export default SsaAttendanceTab;
